import React, { useEffect, useState } from 'react';
import styles from 'components/medicalHistory/page.module.scss';
import Button from 'components/common/button';
import dynamic from 'next/dynamic';
import InputText from 'components/common/InputText';
import {
  StateTypeLocalStorage,
  usePersistMHReducer,
} from 'reducer/medicalHistory';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { PatientDTO } from 'types/patients';
import {
  validateEmail,
  formatDateOnly,
  isEmptyString,
  isValidWeight,
  normalizePatientName,
  parseHeightInFeet,
  validatePatientName,
} from 'utils/helper';
import Label from 'components/common/label';
import dayjs from 'dayjs';
import MetaTag from 'components/meta';
import { patientSex } from 'utils/constants';
import Wrapper from 'components/common/Wrapper';
import MaskedTextField from 'components/common/MaskedTextField';
import { IMask } from 'react-imask';
import range from 'lodash/range';
import isNil from 'lodash/isNil';
import clsx from 'clsx';

const MuiDatePicker = dynamic(() => import('../common/datePicker'), { ssr: false });

const shoeSizeMask = [{
  mask: 'digit.fraction',
  overwrite: true,
  eager: true,
  blocks: {
    digit: {
      mask: IMask.MaskedEnum,
      enum: range(1, 21).map((num) => num.toString()),
    },
    fraction: {
      mask: IMask.MaskedEnum,
      enum: ['0', '5'],
    },
  },
}];

const weightMask = [
  { mask: '' },
  {
    mask: 'weight lbs',
    overwrite: true,
    lazy: false,
    eager: true,
    blocks: {
      weight: {
        mask: Number,
        min: 1,
        max: 599,
        scale: 0,
      },
    },
  },
];

const feetOption = ['Feet', 1, 2, 3, 4, 5, 6, 7];
const inchesOption = ['Inches', 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

const printStateValue = (localValue: string, remoteValue: string = ''): string => (!isEmptyString(localValue) ? localValue : remoteValue);
const defaultValues = {
  firstName: '',
  middleName: '',
  lastName: '',
  email: '',
  gender: '',
  dob: '',
  occupationStartDate: '',
  heightFeet: '',
  heightInches: '',
  weight: '',
  occupation: '',
  shoeSize: null as number | null,
};

const defaultErrors = {
  firstName: false,
  middleName: false,
  lastName: false,
  email: false,
  dob: false,
  heightFeet: false,
  heightInches: false,
  weight: '',
  occupationStartDate: false,
};

type Props = {
  nextPage: number;
  currentPage: number;
  actionPage: string;
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

function PersonalInformationScreen({
  nextPage,
  nextOrSubmitButton,
  actionPage,
}: Props) {
  const [values, setValues] = useState(defaultValues);
  const [errors, setErrors] = useState(defaultErrors);

  const [state, dispatch] = usePersistMHReducer();
  const patient = useSelector((state: RootState) => state.patient) as unknown as PatientDTO;

  useEffect(() => {
    const personalInfo = state.find((page: StateTypeLocalStorage) => page.name === actionPage);
    const value = personalInfo?.value || {};

    if (!isNil(patient) || Object.keys(value)?.length > 0) {
      const { feet, inches } = parseHeightInFeet(patient?.height ?? 0);
      setValues({
        firstName: printStateValue(value?.firstName, patient?.firstName),
        middleName: printStateValue(value?.middleName, patient?.middleName),
        lastName: printStateValue(value?.lastName, patient?.lastName),
        email: printStateValue(value?.email, patient?.email),
        gender: printStateValue(value?.sex, patient?.genderDescription),
        dob: printStateValue(value?.dob, formatDateOnly(patient?.dateOfBirth ?? '')),
        heightFeet: value?.heightFeet ?? feet?.toString() ?? '',
        heightInches: value?.heightInches ?? inches?.toString() ?? '',
        weight: value?.weight ?? patient?.weight?.toString() ?? '',
        occupation: Object.keys(value)?.length > 0 ? value?.occupation : printStateValue(value?.occupation, patient?.occupation ?? ''),
        shoeSize: value?.shoeSize ?? patient?.shoeSize ?? null,
        occupationStartDate: printStateValue(value?.occupationStartDate, patient?.occupationStartDate ?? ''),
      });
    }
  }, [state, patient, actionPage]);

  const handleChange = (name: string, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: false, weight: '' }));
  };

  const handleNext = async () => {
    const newErrors = { ...errors };

    newErrors.firstName = validatePatientName(values.firstName, 'Enter a valid first name') !== true;
    newErrors.middleName = validatePatientName(values.middleName, 'Enter a valid middle name', false) !== true;
    newErrors.lastName = validatePatientName(values.lastName, 'Enter a valid last name') !== true;
    if (!isEmptyString(values.email) && !validateEmail(values.email)) {
      newErrors.email = true;
    }
    if (!dayjs(values.dob).isValid()) {
      newErrors.dob = true;
    }

    const age = dayjs().diff(values.dob, 'years');
    if (age < 19) {
      if (isEmptyString(values.heightFeet)) {
        newErrors.heightFeet = true;
      }
      if (isEmptyString(values.heightInches)) {
        newErrors.heightInches = true;
      }
      if (isEmptyString(values.weight)) {
        newErrors.weight = 'Weight is required under the age 19';
      }
    }

    if (!isEmptyString(values.weight) && !isValidWeight(Number(values.weight))) {
      newErrors.weight = 'Weight range is 1 - 600 lbs';
    }

    if (
      !isEmptyString(values.occupation)
      && (!isEmptyString(values.occupationStartDate)
      && !dayjs(values.occupationStartDate).isValid())) {
      newErrors.occupationStartDate = true;
    }

    setErrors(newErrors);

    const hasError = Object.values(newErrors).some((e) => e);
    if (!hasError) {
      await dispatch({
        type: actionPage,
        name: 'Personal Information',
        value: {
          ...values,
          firstName: normalizePatientName(values.firstName),
          middleName: normalizePatientName(values.middleName),
          lastName: normalizePatientName(values.lastName),
        },
      });
      nextOrSubmitButton(nextPage);
    }
  };

  return (
    <>
      <MetaTag title="Personal Information" />
      <div className="pb-1">
        <div>
          <h2 className={`marginTopZero ${styles.titleText}`}>Personal Information</h2>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col">
              <Label>First Name</Label>
              <InputText
                value={values.firstName}
                onChange={(e) => { handleChange('firstName', e.target.value.trimStart()); }}
                error={errors.firstName}
              />
            </div>
            <div className="flex flex-col">
              <Label>Middle Name</Label>
              <InputText
                value={values.middleName}
                onChange={(e) => { handleChange('middleName', e.target.value.trimStart()); }}
                error={errors.middleName}
              />
            </div>
            <div className="flex flex-col">
              <Label>Last Name</Label>
              <InputText
                value={values.lastName}
                onChange={(e) => { handleChange('lastName', e.target.value.trimStart()); }}
                error={errors.lastName}
              />
            </div>
            <div className="flex flex-col">
              <Label>Email</Label>
              <InputText
                value={values.email}
                onChange={(e) => { handleChange('email', e.target.value.trimStart()); }}
                error={errors.email}
              />
            </div>
            <div className="flex flex-col">
              <Label>Gender Identity</Label>
              <select
                value={values.gender}
                onChange={(e) => { handleChange('gender', e.target.value); }}
                className={styles.select}
              >
                {patientSex.map((name) => (
                  <option
                    key={name}
                    disabled={name === 'Select'}
                    selected={name === 'Select'}
                    defaultValue={name === 'Select' ? 'Select' : ''}
                    value={name}
                  >
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <Label>Date of Birth</Label>
              <MuiDatePicker
                value={dayjs(values.dob)}
                onChange={(val) => { handleChange('dob', val?.toString() ?? ''); }}
                disableFuture
                error={errors.dob}
              />
            </div>
            <div className="flex flex-col">
              <Label>Height</Label>
              <div className="flex gap-4">
                <select
                  value={values.heightFeet}
                  onChange={(e) => { handleChange('heightFeet', e.target.value); }}
                  className={clsx(
                    'w-full',
                    { [styles.selectError]: errors.heightFeet },
                    { [styles.select]: !errors?.heightFeet },
                  )}
                >
                  {feetOption.map((val) => (
                    <option key={val} disabled={val === 'Feet'} value={val}>
                      {val}
                    </option>
                  ))}
                </select>
                <select
                  value={values.heightInches}
                  onChange={(e) => {
                    handleChange('heightInches', e.target.value);
                  }}
                  className={clsx(
                    'w-full',
                    { [styles.selectError]: errors.heightInches },
                    { [styles.select]: !errors?.heightInches },
                  )}
                >
                  {inchesOption.map((val) => (
                    <option key={val} disabled={val === 'Inches'} value={val}>
                      {val}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <Wrapper
              id="weight"
              label="Weight"
              showErrorText={false}
              error={!!errors.weight}
              className="mt-2"
            >
              <MaskedTextField
                unmask
                mask={weightMask}
                value={values.weight ?? ''}
                onAccept={(val: string) => { handleChange('weight', val); }}
                placeholder="e.g 127 lbs"
              />
            </Wrapper>
            <Wrapper
              id="shoe-size "
              label="Shoe Size"
              showErrorText={false}
              className="mt-2"
            >
              <MaskedTextField
                mask={shoeSizeMask}
                value={values.shoeSize?.toFixed(1) ?? ''}
                onAccept={(value: string) => {
                  setValues((prev) => (
                    { ...prev, shoeSize: isEmptyString(value) ? null : Number(value) }
                  ));
                }}
                placeholder="e.g 5.0 or 7.5"
              />
            </Wrapper>
            <div className="flex flex-col">
              <Label>Occupation</Label>
              <InputText
                value={values.occupation}
                onChange={(e) => {
                  setValues((prev) => (
                    {
                      ...prev,
                      occupation: e.target.value,
                      occupationStartDate: isEmptyString(e.target.value) ? '' : prev.occupationStartDate,
                    }
                  ));
                  setErrors((prev) => ({ ...prev, occupationStartDate: false }));
                }}
              />
            </div>

            {!isEmptyString(values.occupation) && (
              <div className="flex flex-col">
                <Label>Occupation Start Date</Label>
                <MuiDatePicker
                  value={dayjs(values.occupationStartDate)}
                  onChange={(val) => { handleChange('occupationStartDate', val?.toString() ?? ''); }}
                  error={errors.occupationStartDate}
                  disableFuture
                />
              </div>
            )}
          </div>
        </div>
        <div className="next-button-fixed">
          <Button onClick={handleNext}>Next</Button>
        </div>
      </div>
    </>
  );
}

export default PersonalInformationScreen;
